import React, { useState, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ZapIcon, SmartphoneIcon, TargetIcon, CheckCircleIcon, ArrowRightIcon, GlobeIcon, SearchIcon, LayoutTemplateIcon, MapPinIcon, StarIcon, ChevronDownIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
import { CodeTypingEffect } from '../components/CodeTypingEffect';
import { AnimatePresence } from 'framer-motion';
const faqs = [{
  question: 'How long does it take to build a new website?',
  answer: 'Most of our Starter and Growth websites are launched within 2-4 weeks. Premium custom builds can take 4-8 weeks depending on the complexity and how quickly we receive your content and feedback.'
}, {
  question: "Do I own the website once it's built?",
  answer: 'Yes, 100%. Unlike some agencies that hold your website hostage on proprietary platforms, we build on standard platforms (like WordPress or React) and hand over full ownership and admin access once the project is paid in full.'
}, {
  question: 'What is Local SEO and why do I need it?',
  answer: 'Local SEO is the process of optimizing your online presence to attract more business from relevant local searches. For home service businesses, this means showing up in the "Google Map Pack" (the top 3 map results) when someone searches for a plumber or roofer in your city.'
}, {
  question: 'Do you write the content for the website?',
  answer: "Yes! We have professional copywriters who specialize in home service industries. We write conversion-focused, SEO-optimized content for all your service pages so you don't have to stare at a blank screen."
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-800 last:border-0" data-id="element-2538">
      <button className="w-full py-6 flex items-center justify-between text-left focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-2539">
        <span className="text-lg font-semibold text-white" data-id="element-2540">{question}</span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} className="flex-shrink-0 ml-4 text-viridia" data-id="element-2541">
          <ChevronDownIcon className="w-5 h-5" data-id="element-2542" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-2543">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-2544">
            <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-2545">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function WebDesignSeoPage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-2546">
      <SEOHead title="Web Design & Local SEO | Phoenix Websites That Convert | Viridia Analytics" description="Fast, mobile-first websites with local SEO that turns Phoenix searches into customers. Speed-optimized, conversion-focused web design with Google Business Profile optimization." canonical="/web-design-seo" keywords="web design Phoenix, local SEO Phoenix, mobile-first website, conversion-focused design, Google Business Profile optimization, local search optimization, website development Phoenix" faq={faqs} data-id="element-2547" />
      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden lg:min-h-[90vh] flex items-center" data-id="element-2548">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-2549">
          <div className="absolute top-[10%] left-[10%] w-[40%] h-[40%] rounded-full bg-viridia/10 blur-[120px] animate-pulse-slow" data-id="element-2550"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-2551">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-2552">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-2553">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-2554">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 border border-viridia/20 text-viridia text-sm font-semibold tracking-wide mb-6" data-id="element-2555">
                  WEB DESIGN & SEO
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6" data-id="element-2556">
                  Turn Searches Into{' '}
                  <span className="text-viridia" data-id="element-2557">Customers</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-2558">
                  We build fast, conversion-focused websites with local SEO that
                  makes sure your business shows up when Phoenix customers are
                  searching for what you do.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-2559">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300" data-id="element-2560">
                    Get a Custom Quote
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-2561">
                    View Pricing
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Illustration - Browser Mockup */}
            <div className="relative hidden lg:block" data-id="element-2562">
              <motion.div initial={{
              opacity: 0,
              y: 40,
              rotateX: 10
            }} animate={{
              opacity: 1,
              y: 0,
              rotateX: 0
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} style={{
              perspective: '1000px'
            }} className="w-full" data-id="element-2563">
                <div className="bg-viridia-surface border border-gray-700 rounded-xl overflow-hidden shadow-2xl transform rotate-y-[-5deg] rotate-z-[2deg]" data-id="element-2564">
                  {/* Browser Chrome */}
                  <div className="bg-gray-900 px-4 py-3 flex items-center border-b border-gray-800" data-id="element-2565">
                    <div className="flex space-x-2" data-id="element-2566">
                      <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-2567"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-2568"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-2569"></div>
                    </div>
                    <div className="mx-auto bg-gray-800 text-gray-400 text-xs px-4 py-1 rounded-md flex items-center" data-id="element-2570">
                      <SearchIcon className="w-3 h-3 mr-2" data-id="element-2571" />
                      phoenix-local-services.com
                    </div>
                  </div>
                  {/* Real-time Coding Effect */}
                  <CodeTypingEffect data-id="element-2572" />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE BUILD SECTION */}
      <section className="py-24 bg-viridia-light" data-id="element-2573">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2574">
          <ScrollReveal data-id="element-2575">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2576">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-2577">
                Websites Built to Convert
              </h2>
              <p className="text-lg text-gray-600" data-id="element-2578">
                Not just pretty - built for speed, mobile-first, and optimized
                to turn visitors into paying customers.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2579">
            <ScrollReveal delay={0.1} data-id="element-2580">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full" data-id="element-2581">
                <div className="w-14 h-14 rounded-xl bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-2582">
                  <ZapIcon className="w-7 h-7" data-id="element-2583" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2584">
                  Speed-Optimized
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2585">
                  Lightning-fast load times that keep visitors engaged and
                  improve your Google ranking. No bloated templates.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-2586">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full" data-id="element-2587">
                <div className="w-14 h-14 rounded-xl bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-2588">
                  <SmartphoneIcon className="w-7 h-7" data-id="element-2589" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2590">
                  Mobile-First Design
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2591">
                  Over 70% of your customers browse on their phone. Every site
                  we build looks and works perfectly on mobile.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3} data-id="element-2592">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full" data-id="element-2593">
                <div className="w-14 h-14 rounded-xl bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-2594">
                  <TargetIcon className="w-7 h-7" data-id="element-2595" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2596">
                  Conversion-Focused
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2597">
                  Strategic layouts, clear CTAs, and trust signals designed to
                  turn visitors into leads and booked jobs.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* LOCAL SEO SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-2598">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2599">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2600">
            <div data-id="element-2601">
              <ScrollReveal data-id="element-2602">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2603">
                  Local SEO That Gets You Found
                </h2>
                <p className="text-lg text-gray-400 mb-8" data-id="element-2604">
                  When someone in Phoenix searches for your service, you need to
                  show up. We make that happen.
                </p>
                <div className="space-y-4" data-id="element-2605">
                  {['Google Business Profile Optimization', 'Local Keyword Targeting', 'On-Page SEO & Meta Tags', 'Schema Markup for Local Businesses', 'Citation Building & NAP Consistency', 'Monthly SEO Reporting'].map((feature, index) => <div key={index} className="flex items-center" data-id="element-2606">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0" data-id="element-2607" />
                      <span className="text-gray-300" data-id="element-2608">{feature}</span>
                    </div>)}
                </div>
              </ScrollReveal>
            </div>

            <div className="relative" data-id="element-2609">
              <ScrollReveal direction="left" data-id="element-2610">
                {/* Mock Google Search Result */}
                <div className="bg-white rounded-xl p-6 shadow-2xl" data-id="element-2611">
                  <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-100" data-id="element-2612">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center" data-id="element-2613">
                      <MapPinIcon className="w-6 h-6 text-gray-500" data-id="element-2614" />
                    </div>
                    <div data-id="element-2615">
                      <h4 className="text-lg font-bold text-blue-800 hover:underline cursor-pointer" data-id="element-2616">
                        Phoenix Premier Services
                      </h4>
                      <div className="flex items-center text-sm text-gray-600" data-id="element-2617">
                        <span className="text-yellow-500 flex items-center mr-2" data-id="element-2618">
                          4.9 <StarIcon className="w-3 h-3 ml-1 fill-current" data-id="element-2619" />
                        </span>
                        (128) · Service Business
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2" data-id="element-2620">
                    <div className="flex items-center text-sm text-gray-600" data-id="element-2621">
                      <MapPinIcon className="w-4 h-4 mr-2 text-gray-400" data-id="element-2622" />
                      Serving Phoenix, Scottsdale, and surrounding areas
                    </div>
                    <div className="flex items-center text-sm text-green-700 font-medium" data-id="element-2623">
                      <CheckCircleIcon className="w-4 h-4 mr-2" data-id="element-2624" />
                      Open 24/7 · Responds in minutes
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100 flex gap-2" data-id="element-2625">
                    <div className="flex-1 bg-blue-50 text-blue-700 text-center py-2 rounded-full text-sm font-medium cursor-pointer hover:bg-blue-100" data-id="element-2626">
                      Website
                    </div>
                    <div className="flex-1 bg-blue-50 text-blue-700 text-center py-2 rounded-full text-sm font-medium cursor-pointer hover:bg-blue-100" data-id="element-2627">
                      Directions
                    </div>
                    <div className="flex-1 bg-blue-50 text-blue-700 text-center py-2 rounded-full text-sm font-medium cursor-pointer hover:bg-blue-100" data-id="element-2628">
                      Call
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* GOOGLE BUSINESS PROFILE SUPPORT */}
      <section className="py-16 bg-viridia-black" data-id="element-2629">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2630">
          <ScrollReveal data-id="element-2631">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-2632">
              <div className="flex items-start gap-4" data-id="element-2633">
                <div className="w-12 h-12 rounded-xl bg-viridia/10 flex items-center justify-center flex-shrink-0" data-id="element-2634">
                  <MapPinIcon className="w-6 h-6 text-viridia" data-id="element-2635" />
                </div>
                <div data-id="element-2636">
                  <h3 className="text-xl font-bold text-white mb-2" data-id="element-2637">
                    Need Help Getting Found on Google Maps?
                  </h3>
                  <p className="text-gray-400 text-sm max-w-xl" data-id="element-2638">
                    If your Google Business Profile isn't set up or verified,
                    you're invisible to local customers. We handle the entire
                    setup and verification process for $397 - so your business
                    is properly listed and ready to go.
                  </p>
                </div>
              </div>
              <Link to="/pricing" className="inline-flex items-center text-viridia hover:text-viridia-dark font-semibold transition-colors whitespace-nowrap" data-id="element-2639">
                View Details <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2640" />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* PORTFOLIO PREVIEW SECTION */}
      <section className="py-24 bg-viridia-light" data-id="element-2641">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2642">
          <ScrollReveal data-id="element-2643">
            <div className="text-center mb-16" data-id="element-2644">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-2645">
                Our Work
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-2646">
                Clean, modern websites that represent your brand and drive
                results.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-2647">
            {[{
            name: 'TFP Landscaping',
            type: 'Landscaping & Outdoor Services',
            url: 'https://www.tfplandscaping.com',
            domain: 'tfplandscaping.com',
            image: 'https://cdn.magicpatterns.com/uploads/pNqDjKbbn559dXQNHJrNcf/image.png'
          }, {
            name: 'Finish Coat Paint AZ',
            type: 'Painting & Contracting',
            url: 'https://www.finishcoatpaintaz.com',
            domain: 'finishcoatpaintaz.com',
            image: 'https://cdn.magicpatterns.com/uploads/2MaqmMhJUBbWkMzmHWrQb7/image.png'
          }].map((project, index) => <ScrollReveal key={index} delay={index * 0.2} data-id="element-2648">
                <a href={project.url} target="_blank" rel="noopener noreferrer" className="group block" data-id="element-2649">
                  <div className="bg-gray-900 rounded-t-xl px-4 py-3 flex items-center border-b border-gray-800" data-id="element-2650">
                    <div className="flex space-x-2" data-id="element-2651">
                      <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-2652"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-2653"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-2654"></div>
                    </div>
                    <div className="mx-auto bg-gray-800 text-gray-400 text-xs px-4 py-1 rounded-md flex items-center" data-id="element-2655">
                      <SearchIcon className="w-3 h-3 mr-2" data-id="element-2656" />
                      {project.domain}
                    </div>
                  </div>
                  <div className="border-x border-b border-gray-200 rounded-b-xl h-72 relative overflow-hidden group-hover:shadow-xl transition-all duration-300" data-id="element-2657">
                    <img src={project.image} alt={`${project.name} website screenshot`} loading="lazy" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" data-id="element-2658" />
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2659">
                      <span className="text-white font-semibold flex items-center" data-id="element-2660">
                        Visit Site <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-2661" />
                      </span>
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-2662">
                    <h4 className="text-xl font-bold text-gray-900" data-id="element-2663">
                      {project.name}
                    </h4>
                    <p className="text-gray-500" data-id="element-2664">{project.type}</p>
                  </div>
                </a>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-2665">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2666">
          <ScrollReveal data-id="element-2667">
            <div className="text-center mb-16" data-id="element-2668">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2669">
                Frequently Asked Questions
              </h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2} data-id="element-2670">
            <div className="bg-viridia-surface rounded-2xl p-6 md:p-8 border border-gray-800" data-id="element-2671">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-2672" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia-dark to-viridia relative overflow-hidden" data-id="element-2673">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-2674"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-2675">
          <ScrollReveal data-id="element-2676">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-2677">
              Ready for a Website That Actually Works?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-2678">
              Get a custom quote for a site built to convert visitors into
              customers.
            </p>
            <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-white text-viridia-dark font-bold text-lg hover:bg-gray-100 transition-colors duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1" data-id="element-2679">
              Get Your Custom Quote
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-2680" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}